import React, { useCallback, useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { usePWA } from "@/context/PWAContext";
import { useToast } from "@/context/ToastContext";
import { cn } from "@/lib/utils";

const SESSION_WARN_MS = 5 * 60 * 1000;

function Banner({ tone, children, action }: { tone: "warn" | "info" | "neutral"; children: React.ReactNode; action?: React.ReactNode }) {
  const style = {
    warn: "border-warning/30 bg-warning-soft text-warning",
    info: "border-primary/20 bg-accent text-primary",
    neutral: "border-border bg-muted text-foreground",
  }[tone];
  return (
    <div role="status" className={cn("flex items-center gap-2.5 border-b px-4 py-2 text-[13px]", style)}>
      <span className="flex-1">{children}</span>
      {action}
    </div>
  );
}

const bannerButton = "h-7 rounded-md px-2.5 text-xs font-semibold";

/** Offline, session ending and new-version notices, across the top of the app */
export function Banners() {
  const { isOnline, hasUpdate, applyUpdate } = usePWA();
  const { error } = useToast();
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);

  const loadSession = useCallback(() => {
    api
      .get("/api/auth/session")
      .then((res) => {
        const at = unwrapObject<{ expires_at: string | null }>(res).expires_at;
        setExpiresAt(at ? new Date(at).getTime() : null);
      })
      .catch(() => undefined);
  }, []);

  useEffect(loadSession, [loadSession]);

  // Wake up when the warning is due, then count down by the minute
  useEffect(() => {
    if (!expiresAt) return;
    const wait = Math.max(0, expiresAt - SESSION_WARN_MS - Date.now());
    const first = setTimeout(() => setNow(Date.now()), wait);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      clearTimeout(first);
      clearInterval(tick);
    };
  }, [expiresAt]);

  const stay = async () => {
    setBusy(true);
    try {
      const res = unwrapObject<{ expires_at: string | null }>(await api.post("/api/auth/refresh"));
      setExpiresAt(res.expires_at ? new Date(res.expires_at).getTime() : null);
      setNow(Date.now());
    } catch (err: any) {
      error("Could not extend your session", err.message || "Please sign in again");
    } finally {
      setBusy(false);
    }
  };

  const left = expiresAt ? expiresAt - now : null;
  const ending = left !== null && left > 0 && left <= SESSION_WARN_MS;
  const minutes = left ? Math.max(1, Math.ceil(left / 60000)) : 0;

  if (isOnline && !hasUpdate && !ending) return null;
  return (
    <div className="shrink-0">
      {!isOnline && <Banner tone="warn">You're offline. Pages you've already opened still show, but changes can't be saved until you're back online.</Banner>}
      {ending && (
        <Banner
          tone="info"
          action={
            <button type="button" onClick={stay} disabled={busy} className={cn(bannerButton, "bg-primary text-primary-foreground disabled:opacity-70")}>
              Stay signed in
            </button>
          }
        >
          Your session ends in {minutes} minute{minutes === 1 ? "" : "s"}.
        </Banner>
      )}
      {hasUpdate && (
        <Banner
          tone="neutral"
          action={
            <button type="button" onClick={applyUpdate} className={cn(bannerButton, "border border-border bg-card font-medium")}>
              Reload
            </button>
          }
        >
          A new version of the CRM is ready.
        </Banner>
      )}
    </div>
  );
}
